import { useCallback, useEffect, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  selectIsVbUploading,
  selectPendingVbImage,
  selectTextToImages,
  selectTextToImagesLoading,
  selectVbImage,
} from '../vb/reducer'
import * as CONSTANTS from './constants'
import { setTextToImageError, setVirtualBackground } from '../vb/actions'
import useLightModePreference from '../util/useLightModePreference'
import NewGeneratedImage from './NewGeneratedImage'
import { IconAICompanionFill, IconLoadingSpinner } from '@zoom/icons-react'
import { GENERATING, SHOWING_RESULTS } from './translation/resources'
import 'rc-dropdown/assets/index.css'
import './NewGeneratedImages.css'

const totalImages = 2
const getGeneratedImageSelectionId = (image, index) =>
  `${image?.id || 'generated-image'}-${index}`
const getImageDataUrl = (image) => image?.src?.dataUrl
const initialImageSelectionState = {
  applyingSelectionId: null,
  confirmedSelectionId: null,
  lastRequestedSelectionId: null,
  queuedSelection: null,
  selectedSelectionId: null,
}
const IMAGE_SELECTION_ACTIONS = {
  APPLY_FAILED: 'APPLY_FAILED',
  APPLY_STARTED: 'APPLY_STARTED',
  APPLY_SUCCEEDED: 'APPLY_SUCCEEDED',
  QUEUE_SELECTION: 'QUEUE_SELECTION',
  RESET: 'RESET',
  SYNC_CONFIRMED_SELECTION: 'SYNC_CONFIRMED_SELECTION',
}
const getSelectionStateLog = (state) => ({
  applyingSelectionId: state.applyingSelectionId,
  confirmedSelectionId: state.confirmedSelectionId,
  lastRequestedSelectionId: state.lastRequestedSelectionId,
  queuedSelectionId: state.queuedSelection?.selectionId || null,
  selectedSelectionId: state.selectedSelectionId,
})
const logSelectionEvent = (event, details = {}) => {
  console.info('[NewGeneratedImages]', {
    event,
    ...details,
  })
}
const imageSelectionReducer = (state, action) => {
  switch (action.type) {
    case IMAGE_SELECTION_ACTIONS.RESET:
      return initialImageSelectionState
    case IMAGE_SELECTION_ACTIONS.SYNC_CONFIRMED_SELECTION: {
      const hasPendingSelection = Boolean(
        state.applyingSelectionId || state.queuedSelection
      )
      const shouldSyncSelectedSelection =
        !state.selectedSelectionId ||
        (!hasPendingSelection &&
          (!state.lastRequestedSelectionId ||
            action.selectionId === state.lastRequestedSelectionId))

      return {
        ...state,
        confirmedSelectionId: action.selectionId,
        selectedSelectionId: shouldSyncSelectedSelection
          ? action.selectionId
          : state.selectedSelectionId,
      }
    }
    case IMAGE_SELECTION_ACTIONS.APPLY_STARTED:
      return {
        ...state,
        applyingSelectionId: action.selectionId,
        lastRequestedSelectionId: action.selectionId,
        queuedSelection:
          state.queuedSelection?.selectionId === action.selectionId
            ? null
            : state.queuedSelection,
        selectedSelectionId: action.selectionId,
      }
    case IMAGE_SELECTION_ACTIONS.APPLY_SUCCEEDED:
      return {
        ...state,
        applyingSelectionId:
          state.applyingSelectionId === action.selectionId
            ? null
            : state.applyingSelectionId,
        confirmedSelectionId: action.selectionId,
        selectedSelectionId:
          state.queuedSelection?.selectionId || action.selectionId,
      }
    case IMAGE_SELECTION_ACTIONS.APPLY_FAILED:
      return {
        ...state,
        applyingSelectionId:
          state.applyingSelectionId === action.selectionId
            ? null
            : state.applyingSelectionId,
        selectedSelectionId:
          state.queuedSelection?.selectionId || action.selectionId,
      }
    case IMAGE_SELECTION_ACTIONS.QUEUE_SELECTION:
      return {
        ...state,
        lastRequestedSelectionId: action.selectionId,
        queuedSelection: {
          image: action.image,
          selectionId: action.selectionId,
        },
        selectedSelectionId: action.selectionId,
      }
    default:
      return state
  }
}
const getStoredSelectionId = (storedImage, images) => {
  if (!storedImage) {
    return null
  }

  if (storedImage.selectionId) {
    return storedImage.selectionId
  }

  const storedDataUrl = getImageDataUrl(storedImage)
  const storedId = storedImage.id
  const matchedIndex = images.findIndex((image) => {
    const imageDataUrl = getImageDataUrl(image)

    if (storedDataUrl && imageDataUrl) {
      return storedDataUrl === imageDataUrl
    }

    return storedId && image.id === storedId
  })

  if (matchedIndex === -1) {
    return null
  }

  return getGeneratedImageSelectionId(images[matchedIndex], matchedIndex)
}

const NewGeneratedImages = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isTextToImagesLoading = useSelector(selectTextToImagesLoading)
  const textToImages = useSelector(selectTextToImages)
  const isVbUploading = useSelector(selectIsVbUploading)
  const pendingVbImage = useSelector(selectPendingVbImage)
  const vbImage = useSelector(selectVbImage)
  const [seconds, setSeconds] = useState(0)
  const [imageSelectionState, dispatchImageSelection] = useReducer(
    imageSelectionReducer,
    initialImageSelectionState
  )
  const loadingTimeOutInSeconds = 60
  const isLightMode = useLightModePreference()
  const pendingSelectionId = isVbUploading
    ? getStoredSelectionId(pendingVbImage, textToImages)
    : null
  const appliedSelectionId = getStoredSelectionId(vbImage, textToImages)
  const effectiveSelectedImageId =
    imageSelectionState.selectedSelectionId ||
    pendingSelectionId ||
    imageSelectionState.confirmedSelectionId ||
    appliedSelectionId

  useEffect(() => {
    if (!isTextToImagesLoading) {
      setSeconds(0)
      return
    }

    let intervalId

    if (seconds >= loadingTimeOutInSeconds) {
      dispatch(setTextToImageError(CONSTANTS.ERROR_MESSAGE_FAILED_TO_GENERATE))
    } else {
      intervalId = window.setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds + 1)
      }, 1000)
    }

    return () => window.clearInterval(intervalId)
  }, [dispatch, isTextToImagesLoading, seconds])

  useEffect(() => {
    logSelectionEvent('reset-for-new-images', {
      imageCount: textToImages.length,
      imageIds: textToImages.map((image) => image.id),
    })
    dispatchImageSelection({ type: IMAGE_SELECTION_ACTIONS.RESET })
  }, [textToImages])

  useEffect(() => {
    if (!appliedSelectionId) {
      return
    }

    logSelectionEvent('sync-confirmed-from-redux', {
      appliedSelectionId,
    })
    dispatchImageSelection({
      type: IMAGE_SELECTION_ACTIONS.SYNC_CONFIRMED_SELECTION,
      selectionId: appliedSelectionId,
    })
  }, [appliedSelectionId])

  const applyImage = useCallback(
    async (image, selectionId) => {
      logSelectionEvent('apply-started', {
        imageId: image?.id || null,
        selectionId,
      })
      dispatchImageSelection({
        type: IMAGE_SELECTION_ACTIONS.APPLY_STARTED,
        selectionId,
      })

      try {
        await dispatch(
          setVirtualBackground({
            image: {
              ...image,
              selectionId,
            },
          })
        ).unwrap()
        logSelectionEvent('apply-succeeded', {
          imageId: image?.id || null,
          selectionId,
        })
        dispatchImageSelection({
          type: IMAGE_SELECTION_ACTIONS.APPLY_SUCCEEDED,
          selectionId,
        })
      } catch (error) {
        logSelectionEvent('apply-failed', {
          error: error?.message || String(error),
          imageId: image?.id || null,
          selectionId,
        })
        dispatchImageSelection({
          type: IMAGE_SELECTION_ACTIONS.APPLY_FAILED,
          selectionId,
        })
      }
    },
    [dispatch]
  )

  useEffect(() => {
    const { applyingSelectionId, queuedSelection } = imageSelectionState

    if (!queuedSelection || applyingSelectionId || isVbUploading) {
      return
    }

    logSelectionEvent('queue-applying-latest', {
      imageId: queuedSelection.image?.id || null,
      selectionId: queuedSelection.selectionId,
      state: getSelectionStateLog(imageSelectionState),
    })
    applyImage(queuedSelection.image, queuedSelection.selectionId)
  }, [applyImage, imageSelectionState, isVbUploading])

  const handleSelectImage = useCallback(
    (image, selectionId) => {
      const isBusy = Boolean(
        imageSelectionState.applyingSelectionId || isVbUploading
      )
      const isCurrentSelection =
        pendingSelectionId === selectionId || appliedSelectionId === selectionId

      if (isBusy) {
        if (imageSelectionState.applyingSelectionId === selectionId) {
          logSelectionEvent('tap-ignored-already-applying', {
            imageId: image?.id || null,
            isVbUploading,
            selectionId,
            state: getSelectionStateLog(imageSelectionState),
          })
          return
        }

        logSelectionEvent('tap-queued', {
          imageId: image?.id || null,
          isVbUploading,
          selectionId,
          state: getSelectionStateLog(imageSelectionState),
        })
        dispatchImageSelection({
          type: IMAGE_SELECTION_ACTIONS.QUEUE_SELECTION,
          image,
          selectionId,
        })
        return
      }

      logSelectionEvent(
        isCurrentSelection
          ? 'tap-reapplying-current-selection'
          : 'tap-applying-immediately',
        {
          appliedSelectionId,
          imageId: image?.id || null,
          pendingSelectionId,
          selectionId,
          state: getSelectionStateLog(imageSelectionState),
        }
      )
      applyImage(image, selectionId)
    },
    [
      appliedSelectionId,
      applyImage,
      imageSelectionState,
      isVbUploading,
      pendingSelectionId,
    ]
  )

  return (
    <div className="new-generated-images">
      <div className="new-generated-images-title">
        <IconAICompanionFill
          style={{ color: isLightMode ? '#000' : '#fff', height: '16px' }}
        />
        <p className="new-generated-images-title-text">
          {isTextToImagesLoading ? t(GENERATING) : t(SHOWING_RESULTS)}
        </p>
      </div>

      <div
        className={`new-generated-images-images ${
          isTextToImagesLoading ? 'loading' : ''
        }`}
      >
        {isTextToImagesLoading
          ? Array.from({ length: totalImages }).map((_, index) => (
              <div key={index} className="loading-image-container">
                <div
                  className={`loading-image ${
                    index + 1 === totalImages ? 'last-loading-image' : ''
                  }`}
                  tabIndex={0}
                >
                  <IconLoadingSpinner className="loading-icon" />
                </div>
              </div>
            ))
          : textToImages.map((image, index) => (
              <NewGeneratedImage
                key={getGeneratedImageSelectionId(image, index)}
                image={image}
                effectiveSelectedImageId={effectiveSelectedImageId}
                index={index}
                selectionId={getGeneratedImageSelectionId(image, index)}
                onSelectImage={handleSelectImage}
                total={totalImages}
              />
            ))}
      </div>
    </div>
  )
}

export default NewGeneratedImages
